import React, { useState } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select"; // Assuming this is available

const MetricsTableOne = ({ strategyWiseTradeMetrics }) => {
  const [selectedAsset, setSelectedAsset] = useState("All");
  const [isOpen, setIsOpen] = useState(false);

  // Find strategies based on the selected asset
  const selectedAssetData = strategyWiseTradeMetrics.find(
    (item) => item.asset === selectedAsset
  );
  const strategiesForSelectedAsset = selectedAssetData
    ? selectedAssetData.strategy_wise_metrics.map((item) => item.strategy)
    : [];

  const [selectedStrategies, setSelectedStrategies] = useState(
    strategiesForSelectedAsset
  );

  // Handle filtering strategies by checkbox
  const toggleStrategy = (strategy) => {
    setSelectedStrategies((prevSelected) =>
      prevSelected.includes(strategy)
        ? prevSelected.filter((item) => item !== strategy)
        : [...prevSelected, strategy]
    );
  };

  // Filtered data based on selected strategies and asset
  const filteredData = selectedAssetData
    ? selectedAssetData.strategy_wise_metrics.filter((item) =>
        selectedStrategies.includes(item.strategy)
      )
    : [];

  return (
    <div className="py-2">
      {/* Asset Select Dropdown */}

      <div className="w-full flex items-center justify-end px-3 py-2">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <div className="text-xl font-semibold leading-none tracking-tight">
            Strategy wise Metrics Data Table
          </div>
        </div>
        <div className="flex items-center justify-end px-3 py-2">
          <div className="w-[150px] mr-4">
            <Select
              onValueChange={(value) => {
                setSelectedAsset(value);
                setSelectedStrategies(
                  strategyWiseTradeMetrics
                    .find((item) => item.asset === value)
                    ?.strategy_wise_metrics.map((item) => item.strategy) || []
                );
              }}
              defaultValue="All"
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Asset" />
              </SelectTrigger>
              <SelectContent>
                {strategyWiseTradeMetrics.map((data) => (
                  <SelectItem className="" key={data.asset} value={data.asset}>
                    {data.asset}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Strategy Dropdown */}
          <DropdownMenu onOpenChange={(open) => setIsOpen(open)}>
            <DropdownMenuTrigger className="px-3 py-2 bg-muted rounded-[4px] text-[14px] flex items-center">
              Select Strategies
              {isOpen ? (
                <ChevronUpIcon className="ml-2" />
              ) : (
                <ChevronDownIcon className="ml-2" />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Strategies</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {strategiesForSelectedAsset.map((strategy) => (
                <DropdownMenuItem
                  key={strategy}
                  onClick={() => toggleStrategy(strategy)}
                >
                  <input
                    type="checkbox"
                    checked={selectedStrategies.includes(strategy)}
                    readOnly
                  />
                  <span className="ml-2">{strategy}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table Display */}
      <div className="mt-4 overflow-auto custom-scroll h-[480px]">
        <Table className="">
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead className="text-left text-[12px]">Strategy</TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Total PNL (₹)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Accuracy (%)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Total Trades
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                (+ve) Trades
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                (-ve) Trades
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Win Trades (₹)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Loss Trades (₹)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[150px] text-[12px]">
                Win Day Avg. Profit (₹)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[150px] text-[12px]">
                Loss Day Avg. Loss (₹)
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                RR Ratio
              </TableHead>
              <TableHead className="text-right py-4 min-w-[100px] text-[12px]">
                Expectancy
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="text-left">{item.strategy}</TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.totalPNL > 0 ? "text-teal-400" : "text-rose-400"
                  }`}
                >
                  {Math.round(item.totalPNL)}
                </TableCell>
                <TableCell className="text-right py-4">
                  {Math.round(item.accuracy)}
                </TableCell>
                <TableCell className="text-right py-4">
                  {item.totalTrades}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.positiveTrades > 0 ? "text-teal-400" : "text-rose-400"
                  }`}
                >
                  {item.positiveTrades}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.negativeTrades > 0 ? "text-teal-400" : "text-rose-400"
                  }`}
                >
                  {item.negativeTrades}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.winTradesRS > 0 ? "text-teal-400" : "text-rose-400"
                  }`}
                >
                  {Math.round(item.winTradesRS)}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.lossTradesRS > 0 ? "text-teal-400" : "text-rose-400"
                  }`}
                >
                  {Math.round(item.lossTradesRS)}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.avgProfitPerWinTrades > 0
                      ? "text-teal-400"
                      : "text-rose-400"
                  }`}
                >
                  {Math.round(item.avgProfitPerWinTrades)}
                </TableCell>
                <TableCell
                  className={`text-right py-4 ${
                    item.avgLossPerLossTrades > 0
                      ? "text-teal-400"
                      : "text-rose-400"
                  }`}
                >
                  {Math.round(item.avgLossPerLossTrades)}
                </TableCell>
                <TableCell className="text-right py-4">
                  {Math.round(item.rrRatio)}
                </TableCell>
                <TableCell className="text-right py-4 text-blue-500">
                  {Math.round(item.expectancy)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MetricsTableOne;
